import { useDispatch, useSelector } from "react-redux";

import { is } from "app/pages/sales-intelligence/utils/type-guards";
import type { RootState } from "single-spa/store/types";

import { toggleExpandedRow } from "../../store/action-creators";
import { selectIsRowExpanded } from "../../store/selectors";
import type { ExpandedRowVariant } from "../../types/expanded-row";
import type { TableRowKey } from "../../types/table";

type HookValue = {
    isExpanded: boolean;
    onToggle: VoidFunction;
};

/**
 * Co-locates the expand toggle and the derived expanded state for a single row so the
 * (key, variant) pair is declared once. `onToggle` dispatches; `isExpanded` subscribes to a
 * derived boolean so a toggle only re-renders the cells whose expansion actually flips.
 */
export const useExpandableRow = (key: TableRowKey | undefined, variant: ExpandedRowVariant): HookValue => {
    const dispatch = useDispatch();
    const isExpanded = useSelector((state: RootState) => selectIsRowExpanded(state, key, variant));

    const onToggle = (): void => {
        if (is.undefined(key)) {
            return;
        }

        dispatch(toggleExpandedRow({ key, variant }));
    };

    return { isExpanded, onToggle };
};
